import { createAsyncThunk, createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { DocumentType } from "~/config/documents";
import { AuthApi, type SigninResponse } from "~/functions/api/auth";
import { initCurrentUser, initOrganization, initUserInfo } from "~/functions/functions";
import type { Organization } from "~/interface/organization";
import type { Project, ProjectMember } from "~/interface/project";
import type { User, UserData } from "~/interface/user";
import type { UserInfo } from "~/interface/userInfo";

export const fetchUserInfo = createAsyncThunk<SigninResponse, void, { rejectValue: string }>(
  "auth/fetchUser",
  async (_, thunkAPI) => {
    const authApi = new AuthApi();
    const response = await authApi.signin();

    if (response.type === "error") {
      return thunkAPI.rejectWithValue("ユーザー情報取得に失敗しました");
    }

    return response;
  },
);

// authスライス
export interface AuthState {
  currentUserInfo: UserInfo;
  currentUser: User;
  status: "idle" | "loading" | "failed";
  currentProject: Project;
  projectMembers: ProjectMember[];
  organization: Organization;
  signupAllowed: boolean;
  intendedUrl: string;
  tour_completed: boolean;
  tour_step: number;
}

const initialEstimationData = {
  total_cost: 0,
  unit_prices: {
    common: 0,
    app: 0,
    plugin: 0,
    customize: 0,
    external_service: 0,
  },
  work_loads: {
    xl: 0,
    l: 0,
    m: 0,
    s: 0,
    xs: 0,
  },
};

const initialState: AuthState = {
  currentUserInfo: initUserInfo(),
  currentUser: initCurrentUser(),
  status: "idle",
  currentProject: {
    id: "",
    organization_id: "",
    name: "",
    description: undefined,
    start_date: undefined,
    end_date: undefined,
    client: {
      name: undefined,
      url: undefined,
    },
    vocabulary: [],
    outputs: {},
    meta_prompts: [],
    estimationData: { ...initialEstimationData },
    editableEstimationData: { ...initialEstimationData },
    costEffectivenessData: undefined,
  },
  projectMembers: [],
  organization: initOrganization(),
  signupAllowed: false,
  intendedUrl: "",
  tour_completed: false,
  tour_step: 0,
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    clearAuth: (state) => {
      state.currentUserInfo = initUserInfo();
      state.currentUser = initCurrentUser();
      state.organization = initOrganization();
      state.signupAllowed = false;
      state.intendedUrl = "";
    },
    setSignupAllowed: (state, action: PayloadAction<boolean>) => {
      state.signupAllowed = action.payload;
    },
    setCurrentUserInfo(state, action: PayloadAction<UserInfo>) {
      state.currentUserInfo = action.payload;
    },
    setOrganization(state, action: PayloadAction<Organization>) {
      state.organization = action.payload;
    },
    setCurrentUser(state, action: PayloadAction<User>) {
      state.currentUser = action.payload;
    },
    setCurrentUserData(state, action: PayloadAction<UserData>) {
      state.currentUser.last_name = action.payload.last_name;
      state.currentUser.first_name = action.payload.first_name;
      state.currentUser.display_name = action.payload.display_name;
    },
    setCurrentProject(state, action) {
      const project = action.payload;
      if (project) {
        state.currentProject = {
          ...project,
          estimationData: {
            ...project.estimationData,
          },
          editableEstimationData: {
            ...project.estimationData,
          },
        };
      } else {
        state.currentProject = project;
      }
    },
    setProjectMembers(state, action: PayloadAction<ProjectMember[]>) {
      state.projectMembers = action.payload;
    },
    setCurrentProjectOutputsDocumentType(
      state,
      action: PayloadAction<{
        documentType: DocumentType;
        documentId?: string | undefined;
        isGenerating?: boolean | undefined;
      }>,
    ) {
      if (state.currentProject.outputs) {
        state.currentProject.outputs[action.payload.documentType] = {
          id: action.payload.documentId ?? "",
          isGenerating: action.payload.isGenerating ?? false,
        };
      }
    },
    setIntendedUrl(state, action: PayloadAction<string>) {
      state.intendedUrl = action.payload;
      if (typeof window !== "undefined") {
        sessionStorage.setItem("intendedUrl", action.payload);
      }
    },
    clearIntendedUrl(state) {
      state.intendedUrl = "";
      if (typeof window !== "undefined") {
        sessionStorage.removeItem("intendedUrl");
      }
    },
    loadIntendedUrlFromStorage(state) {
      if (typeof window !== "undefined") {
        const storedUrl = sessionStorage.getItem("intendedUrl");
        if (storedUrl) {
          state.intendedUrl = storedUrl;
        }
      }
    },
    setTourCompleted(state, action: PayloadAction<boolean>) {
      state.tour_completed = action.payload;
    },
    setTourStep(state, action: PayloadAction<number>) {
      state.tour_step = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUserInfo.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchUserInfo.fulfilled, (state, action: PayloadAction<SigninResponse>) => {
        const response = action.payload;

        switch (response.type) {
          case "authenticated":
            state.currentUserInfo = response.userInfo;
            state.status = "idle";
            break;
          case "needs_signup":
            // 新規ユーザー: stateは更新せず、signinページで処理
            state.status = "idle";
            break;
          case "needs_signup_invitation":
            // 招待ユーザー: userInfoを保存（signinページで使用）
            state.currentUserInfo = response.userInfo;
            state.status = "idle";
            break;
          default:
            state.status = "failed";
        }
      })
      .addCase(fetchUserInfo.rejected, (state) => {
        state.status = "failed";
      });
  },
});

export const {
  clearAuth,
  setCurrentUserInfo,
  setOrganization,
  setCurrentUser,
  setCurrentUserData,
  setCurrentProject,
  setProjectMembers,
  setCurrentProjectOutputsDocumentType,
  setSignupAllowed,
  setIntendedUrl,
  clearIntendedUrl,
  loadIntendedUrlFromStorage,
  setTourCompleted,
  setTourStep,
} = authSlice.actions;
export default authSlice.reducer;
